import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { useQueue } from '@/components/QueueContext';
import { DOMAIN_COLORS, APP_COLORS } from '@/lib/constants';
import { useConfirmation } from '@/components/shared/ZCard';
import { Plus, Mic, MicOff, Loader2, Trash2, ChevronDown, ChevronUp, Search, X, Paperclip, Copy, Pencil, Upload, Send, Calendar, Check, RotateCcw } from 'lucide-react';
import OverflowMenu from '@/components/shared/OverflowMenu';
import ZFilterPills from '@/components/shared/ZFilterPills';
import FilterToggle from '@/components/shared/FilterToggle';
import ZEmptyState from '@/components/shared/ZEmptyState';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';

// Render text with clickable links
const renderWithLinks = (text) => {
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer"
        onClick={e => e.stopPropagation()}
        style={{ color: '#60A5FA' }}
        className="underline underline-offset-2 decoration-[#60A5FA]/70 hover:opacity-80 transition-opacity break-all">
        {part}
      </a>
    ) : part
  );
};

// Urgency colours
const URGENCY_COLORS = {
  hot: '#E8450A',
  cold: '#4A9CC8',
  // backward-compat for existing DB entries
  burning: '#E8450A',
  warm: '#4A9CC8',
};

const TYPE_COLORS = {
  idea: '#6366F1',
  task: '#2A9D8F'
};

// Cycle arrays
const URGENCY_CYCLE = ['hot', 'cold'];
const TYPE_CYCLE = ['idea', 'task'];
const DOMAIN_CYCLE = ['soul', 'matrix'];

// Helper to get options for attribute
const getOptionsForAttr = (attr) => {
  if (attr === 'urgency') return URGENCY_CYCLE;
  if (attr === 'entry_type') return TYPE_CYCLE;
  if (attr === 'domain') return DOMAIN_CYCLE;
  return [];
};

// Helper to get next value in cycle
const getNextInCycle = (attr, currentValue) => {
  const cycle = getOptionsForAttr(attr);
  const idx = cycle.indexOf(currentValue);
  return cycle[(idx + 1) % cycle.length];
};

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// AttributeMenu component - simplified for mobile
function AttributeMenu({ entry, attr, onUpdate, onCycle, isOpen, onOpenChange }) {
  const menuRef = useRef(null);

  const value = entry[attr];
  const options = getOptionsForAttr(attr);
  const colorMap = attr === 'urgency' ? URGENCY_COLORS : attr === 'entry_type' ? TYPE_COLORS : DOMAIN_COLORS;
  const color = colorMap[value] || 'rgba(154, 154, 154, 0.7)';

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onOpenChange(false);
      }
    };
    document.addEventListener('pointerdown', handleClick);
    return () => document.removeEventListener('pointerdown', handleClick);
  }, [isOpen, onOpenChange]);

  const handleTap = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const isDesktop = window.innerWidth > 768;
    if (isDesktop) {
      onOpenChange(!isOpen);
    } else {
      onCycle(entry.id, attr);
    }
  };
  
  return (
    <div className="relative" ref={menuRef}>
      <button
        onPointerDown={handleTap}
        className="text-[10px] font-mono transition-opacity active:opacity-60 select-none touch-manipulation"
        style={{ color }}
      >
        {value}
      </button>
      
      {isOpen && (
        <div className="absolute right-0 top-5 z-50 bg-white/[0.05] border border-white/[0.06] rounded-lg py-1 shadow-xl min-w-[80px]">
          {options.map(opt => (
            <button
              key={opt}
              onPointerDown={(e) => { e.stopPropagation(); onUpdate(entry.id, attr, opt); onOpenChange(false); }}
              className="block w-full text-left px-3 py-1.5 text-[11px] font-mono hover:bg-white/[0.06] transition-colors"
              style={{ color: colorMap[opt] || (opt === value ? '#f0f0f5' : '#b0b4c0') }}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Inline tooltip wrapper ────────────────────────────────────────────────────
function Tip({ label, children }) {
  const [show, setShow] = useState(false);
  return (
    <div
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <div style={{
          position: 'absolute',
          bottom: 'calc(100% + 6px)',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(255,255,255,0.06)',
          border: '0.5px solid rgba(255,255,255,0.1)',
          borderRadius: 5,
          padding: '3px 8px',
          whiteSpace: 'nowrap',
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 10,
          letterSpacing: '0.06em',
          color: 'rgba(240,240,245,0.7)',
          pointerEvents: 'none',
          zIndex: 50,
        }}>
          {label}
        </div>
      )}
    </div>
  );
}

const GIRAFFE_GREEN = APP_COLORS.giraffe; // #10B981

function TodoPanel({ todos, doneTodos, onToggleDone, onDelete, onAdd, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [newText, setNewText] = useState('');
  const [showDone, setShowDone] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const inputRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (expanded && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [expanded]);

  const handleAdd = () => {
    const text = newText.trim();
    if (!text) return;
    onAdd(text);
    setNewText('');
    inputRef.current?.focus();
  };

  const startEdit = (todo) => {
    setEditingId(todo.id);
    setEditText(todo.title || '');
  };

  const saveEdit = (todoId) => {
    const text = editText.trim();
    if (text && text !== (todos.concat(doneTodos)).find(t => t.id === todoId)?.title) {
      onUpdate(todoId, { title: text });
    }
    setEditingId(null);
  };

  const maxCollapsed = 5;
  const visibleCollapsed = todos.slice(0, maxCollapsed);
  const moreCount = Math.max(0, todos.length - maxCollapsed);

  if (todos.length === 0 && doneTodos.length === 0) return null;

  return (
    <>
      {/* Collapsed todo section */}
      <div
        onClick={() => setExpanded(true)}
        style={{
          background: `rgba(16, 185, 129, 0.04)`,
          border: `1px solid rgba(16, 185, 129, 0.12)`,
          borderRadius: 14,
          padding: '14px 16px',
          marginBottom: 16,
          cursor: 'pointer',
          transition: 'border-color 150ms ease',
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.25)'}
        onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.12)'}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: todos.length > 0 ? 10 : 0 }}>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: GIRAFFE_GREEN, fontWeight: 500 }}>
            TODO
          </span>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'rgba(16, 185, 129, 0.5)' }}>
            {todos.length} active{doneTodos.length > 0 ? ` / ${doneTodos.length} done` : ''}
          </span>
        </div>
        {visibleCollapsed.map(todo => (
          <div key={todo.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', border: `1.5px solid ${GIRAFFE_GREEN}`, flexShrink: 0 }} />
            <span style={{ fontSize: 13, fontWeight: 300, color: 'rgba(240,240,245,0.75)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {todo.title || 'Untitled'}
            </span>
          </div>
        ))}
        {moreCount > 0 && (
          <p style={{ fontSize: 11, color: 'rgba(16, 185, 129, 0.4)', fontFamily: "'JetBrains Mono', monospace", marginTop: 4, paddingLeft: 14 }}>
            + {moreCount} more
          </p>
        )}
      </div>

      {/* Expanded overlay */}
      {expanded && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 40 }}
          onClick={(e) => { if (e.target === e.currentTarget) setExpanded(false); }}
        >
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }} />
          <div
            ref={panelRef}
            onClick={e => e.stopPropagation()}
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: 520,
              maxHeight: 'calc(100vh - 80px)',
              overflowY: 'auto',
              background: '#0c0c12',
              border: `1px solid rgba(16, 185, 129, 0.15)`,
              borderRadius: 16,
              padding: '20px 20px 16px',
              margin: '0 16px',
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: GIRAFFE_GREEN, fontWeight: 500 }}>
                TODO
              </span>
              <button
                onClick={() => setExpanded(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(176,180,192,0.4)', padding: 4 }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Add input */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input
                ref={inputRef}
                value={newText}
                onChange={e => setNewText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
                placeholder="Add a todo..."
                style={{
                  flex: 1,
                  background: 'rgba(16, 185, 129, 0.06)',
                  border: '1px solid rgba(16, 185, 129, 0.15)',
                  borderRadius: 10,
                  padding: '10px 14px',
                  color: '#f0f0f5',
                  fontSize: 14,
                  fontWeight: 300,
                  outline: 'none',
                  fontFamily: 'system-ui, sans-serif',
                }}
              />
              <button
                onClick={handleAdd}
                disabled={!newText.trim()}
                style={{
                  background: newText.trim() ? GIRAFFE_GREEN : 'rgba(16, 185, 129, 0.15)',
                  color: newText.trim() ? '#fff' : 'rgba(16, 185, 129, 0.4)',
                  border: 'none',
                  borderRadius: 10,
                  width: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: newText.trim() ? 'pointer' : 'default',
                  transition: 'all 150ms ease',
                }}
              >
                <Plus size={18} />
              </button>
            </div>

            {/* Active todos */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {todos.length === 0 && (
                <p style={{ fontSize: 13, color: 'rgba(176,180,192,0.3)', fontStyle: 'italic', textAlign: 'center', padding: '12px 0' }}>
                  No active todos
                </p>
              )}
              {todos.map(todo => (
                <div
                  key={todo.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    background: 'rgba(16, 185, 129, 0.04)',
                    border: '1px solid rgba(16, 185, 129, 0.10)',
                    borderLeft: `2px solid ${GIRAFFE_GREEN}`,
                    borderRadius: 8,
                    padding: '8px 10px',
                  }}
                >
                  {/* Done button */}
                  <button
                    onClick={() => onToggleDone(todo.id, 'solved')}
                    style={{
                      flexShrink: 0, width: 20, height: 20, borderRadius: '50%',
                      border: `1.5px solid rgba(16, 185, 129, 0.4)`,
                      background: 'transparent',
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 150ms ease',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(16, 185, 129, 0.15)'; e.currentTarget.style.borderColor = GIRAFFE_GREEN; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.4)'; }}
                  />
                  {/* Text */}
                  {editingId === todo.id ? (
                    <input
                      autoFocus
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      onBlur={() => saveEdit(todo.id)}
                      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingId(null); }}
                      style={{
                        flex: 1, background: 'transparent', border: 'none', borderBottom: '1px solid rgba(16, 185, 129, 0.3)',
                        color: '#f0f0f5', fontSize: 13, fontWeight: 300, outline: 'none', padding: '2px 0',
                      }}
                    />
                  ) : (
                    <span
                      onClick={() => startEdit(todo)}
                      style={{ flex: 1, fontSize: 13, fontWeight: 300, color: 'rgba(240,240,245,0.8)', cursor: 'text', lineHeight: 1.4 }}
                    >
                      {todo.title || 'Untitled'}
                    </span>
                  )}
                  {/* Date */}
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'rgba(176,180,192,0.3)', flexShrink: 0 }}>
                    {formatDate(todo.created_at)}
                  </span>
                  {/* Delete */}
                  <button
                    onClick={() => onDelete(todo.id)}
                    style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(176,180,192,0.2)', padding: 2, transition: 'color 150ms' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#E8450A'}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(176,180,192,0.2)'}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>

            {/* Done section */}
            {doneTodos.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <button
                  onClick={() => setShowDone(d => !d)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6,
                    width: '100%', padding: '8px 0',
                    borderTop: '0.5px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <ChevronDown size={12} style={{ color: 'rgba(176,180,192,0.3)', transform: showDone ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: '0.1em', color: 'rgba(176,180,192,0.35)' }}>
                    Done ({doneTodos.length})
                  </span>
                </button>
                {showDone && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
                    {doneTodos.map(todo => (
                      <div
                        key={todo.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '6px 10px',
                          opacity: 0.5,
                          borderRadius: 8,
                        }}
                      >
                        {/* Filled check */}
                        <button
                          onClick={() => onToggleDone(todo.id, 'open')}
                          title="Recall"
                          style={{
                            flexShrink: 0, width: 20, height: 20, borderRadius: '50%',
                            border: 'none', background: GIRAFFE_GREEN,
                            cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                            <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 300, color: 'rgba(240,240,245,0.4)', textDecoration: 'line-through', lineHeight: 1.4 }}>
                          {todo.title || 'Untitled'}
                        </span>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'rgba(176,180,192,0.2)', flexShrink: 0 }}>
                          {formatDate(todo.updated_at || todo.created_at)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// Entry Card with inline expansion
function EntryCard({
  entry,
  isExpanded,
  onToggle,
  editBody,
  setEditBody,
  onBodyBlur,
  onSaveEditBody,
  onUpdateAttr,
  onCycleAttr,
  onToggleSolved,
  onDelete,
  onDictate,
  dictatePending,
  isRecordingDictate,
  onCalendarTap,
  calendarForm,
  onCalendarFormChange,
  onCreateICS,
  onAttachDoc,
  docAttaching,
  feedback,
}) {
  const isSolved = entry.status === 'solved';
  const urgencyColor = isSolved ? 'rgba(255,255,255,0.10)' : (URGENCY_COLORS[entry.urgency] || URGENCY_COLORS.cold);
  const textareaRef = useRef(null);
  const cardRef = useRef(null);
  const bodyContainerRef = useRef(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [openAttr, setOpenAttr] = useState(null);
  const [rawEdit, setRawEdit] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showAttrDrawer, setShowAttrDrawer] = useState(false);
  // Local copy of body for immediate visual updates (checkbox toggles, etc.)
  const [localBody, setLocalBody] = useState(editBody);

  // Auto-resize textarea and scroll into view when expanded
  useEffect(() => {
    if (isExpanded) {
      // Delay slightly so textarea has rendered before measuring
      const raf = requestAnimationFrame(() => {
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
          textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
        }
      });
      // Only scroll if card is not fully visible
      if (cardRef.current) {
        setTimeout(() => {
          const rect = cardRef.current.getBoundingClientRect();
          const isFullyVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
          if (!isFullyVisible) {
            cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
      return () => cancelAnimationFrame(raf);
    } else {
      setRawEdit(false);
      setEditingTitle(false);
    }
  }, [isExpanded, editBody, rawEdit]);

  // Keep localBody in sync when parent updates editBody (dictate, expand)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setLocalBody(editBody); }, [editBody]);

  if (isExpanded) {
    return (
      <div
        ref={cardRef}
        className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 scroll-mt-24"
        style={{ position: 'relative', borderLeft: `2px solid ${dragOver ? '#9B6DD4' : urgencyColor}`, borderRight: entry.domain && DOMAIN_COLORS[entry.domain] ? `3px solid ${DOMAIN_COLORS[entry.domain]}80` : undefined, opacity: isSolved ? 0.45 : 1, outline: dragOver ? '1.5px dashed #9B6DD455' : 'none' }}
        data-testid={`entry-${entry.id}`}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
        onDragLeave={(e) => { e.stopPropagation(); setDragOver(false); }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragOver(false);
          const file = e.dataTransfer.files[0];
          if (!file) return;
          const isImage = file.type.startsWith('image/');
          const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
          if (!isImage && !isPdf) { return; }
          onAttachDoc(entry.id, file);
        }}
      >
        {/* Title row */}
        <div className="flex items-center gap-2 mb-2">
          {editingTitle ? (
            <input
              autoFocus
              defaultValue={entry.title || ''}
              onBlur={(e) => { if (e.target.value !== entry.title) onUpdateAttr(entry.id, 'title', e.target.value); setEditingTitle(false); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') e.target.blur(); }}
              placeholder="Untitled"
              className="flex-1 min-w-0 bg-transparent text-sm font-medium text-[#f0f0f5] outline-none border-b border-white/[0.06] placeholder:text-[#b0b4c0]/30"
            />
          ) : (
            <p onClick={onToggle} className="flex-1 min-w-0 text-sm font-medium cursor-pointer truncate select-none text-[#f0f0f5]">
              {entry.title || 'Untitled'}
            </p>
          )}
          <button onClick={onToggle} className="text-[#b0b4c0]/30 hover:text-[#b0b4c0]/60 transition-colors shrink-0">
            <ChevronUp size={14} />
          </button>
        </div>
        
        {/* Body: inline checkbox view or raw textarea */}
        {(() => {
          const lines = localBody.split('\n');

          const toggleCheckbox = (i) => {
            const updated = [...lines];
            const line = updated[i];
            if (line.startsWith('[] ')) updated[i] = '[x] ' + line.slice(3);
            else if (line.startsWith('[x] ')) updated[i] = '[] ' + line.slice(4);
            const newBody = updated.join('\n');
            setLocalBody(newBody);  // immediate visual update
            setEditBody(newBody);   // keep parent in sync
            onSaveEditBody(newBody);
          };

          if (!rawEdit) {
            return (
              <div className="mb-1" ref={bodyContainerRef}>
                <div>
                  {lines.map((line, i) => {
                    const isUnchecked = line.startsWith('[] ');
                    const isChecked = line.startsWith('[x] ');
                    if (isUnchecked || isChecked) {
                      const text = isUnchecked ? line.slice(3) : line.slice(4);
                      return (
                        <div key={i} data-line-index={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 4 }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleCheckbox(i); }}
                            style={{
                              flexShrink: 0, width: 18, height: 18, borderRadius: '50%',
                              border: isUnchecked ? '1.5px solid #454860' : 'none',
                              background: isChecked ? '#6B35B8' : 'transparent',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              cursor: 'pointer', padding: 7, marginTop: 2,
                              boxSizing: 'content-box',
                            }}
                          >
                            {isChecked && (
                              <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                                <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </button>
                          <span
                            onClick={() => setRawEdit(true)}
                            style={{
                              flex: 1, fontSize: 14, fontWeight: 300, lineHeight: '1.6',
                              color: isChecked ? '#454860' : 'rgba(240,240,245,0.8)',
                              textDecoration: isChecked ? 'line-through' : 'none',
                              cursor: 'text',
                            }}
                          >
                            {text}
                          </span>
                        </div>
                      );
                    }
                    if (!line.trim()) return <div key={i} data-line-index={i} style={{ height: 8 }} />;
                    return (
                      <p key={i} data-line-index={i} onClick={() => setRawEdit(true)} style={{ fontSize: 14, fontWeight: 300, color: 'rgba(240,240,245,0.6)', lineHeight: '1.6', cursor: 'text', marginBottom: 4 }}>
                        {renderWithLinks(line)}
                      </p>
                    );
                  })}
                  {!editBody.trim() && (
                    <p onClick={() => setRawEdit(true)} style={{ fontSize: 14, color: 'rgba(154,154,154,0.3)', fontStyle: 'italic', cursor: 'text' }}>
                      Write something...
                    </p>
                  )}
                </div>
              </div>
            );
          }

          return (
            <Textarea
              ref={textareaRef}
              autoFocus
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              onBlur={() => { onBodyBlur(); setRawEdit(false); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  const pos = e.target.selectionStart;
                  const text = editBody;
                  const beforeCursor = text.slice(0, pos);
                  const lineStart = beforeCursor.lastIndexOf('\n') + 1;
                  const lineEndIdx = text.indexOf('\n', pos);
                  const currentLine = text.slice(lineStart, lineEndIdx === -1 ? undefined : lineEndIdx);
                  const isUnchecked = currentLine.startsWith('[] ');
                  const isChecked = currentLine.startsWith('[x] ');
                  if (isUnchecked || isChecked) {
                    const prefixLen = isUnchecked ? 3 : 4;
                    const lineContent = currentLine.slice(prefixLen);
                    if (!lineContent.trim()) {
                      e.preventDefault();
                      const newBody = text.slice(0, lineStart) + text.slice(lineStart + prefixLen);
                      setEditBody(newBody);
                      setTimeout(() => { e.target.selectionStart = e.target.selectionEnd = lineStart; }, 0);
                    } else {
                      e.preventDefault();
                      const newBody = text.slice(0, pos) + '\n[] ' + text.slice(pos);
                      setEditBody(newBody);
                      setTimeout(() => { e.target.selectionStart = e.target.selectionEnd = pos + 4; }, 0);
                    }
                  }
                }
                if (e.key === 'Backspace' && e.target.selectionStart === e.target.selectionEnd) {
                  const pos = e.target.selectionStart;
                  const text = editBody;
                  const beforeCursor = text.slice(0, pos);
                  const lineStart = beforeCursor.lastIndexOf('\n') + 1;
                  const lineEndIdx = text.indexOf('\n', pos);
                  const currentLine = text.slice(lineStart, lineEndIdx === -1 ? undefined : lineEndIdx);
                  const isUnchecked = currentLine.startsWith('[] ');
                  const isChecked = currentLine.startsWith('[x] ');
                  if (isUnchecked || isChecked) {
                    const prefixLen = isUnchecked ? 3 : 4;
                    if (!currentLine.slice(prefixLen).trim() && pos === lineStart + prefixLen) {
                      e.preventDefault();
                      const newBody = text.slice(0, lineStart) + text.slice(lineStart + prefixLen);
                      setEditBody(newBody);
                      setTimeout(() => { e.target.selectionStart = e.target.selectionEnd = lineStart; }, 0);
                    }
                  }
                }
              }}
              className="bg-transparent border-none text-[#f0f0f5]/80 font-light text-sm leading-relaxed p-0 focus-visible:ring-0 resize-none min-h-[40px] w-full"
              placeholder="Write something..."
              style={{ fontSize: '16px' }}
            />
          );
        })()}

        {/* Dictate button */}
        <div className="mt-3">
          {isRecordingDictate ? (
            /* Recording: full-width stop bar */
            <button
              onClick={onDictate}
              className="w-full flex items-center justify-center gap-3 py-3 rounded-xl bg-[#E8450A]/15 border border-[#E8450A]/40 transition-all active:scale-[0.98]"
            >
              <div className="flex items-center gap-[3px] h-[18px]">
                <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '60%', animationDelay: '0ms' }} />
                <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '100%', animationDelay: '150ms' }} />
                <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '40%', animationDelay: '300ms' }} />
                <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '80%', animationDelay: '450ms' }} />
              </div>
              <span className="text-sm font-mono text-[#E8450A] font-medium tracking-wide">STOP</span>
              <div className="flex items-center gap-[3px] h-[18px]">
                <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '80%', animationDelay: '100ms' }} />
                <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '40%', animationDelay: '250ms' }} />
                <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '100%', animationDelay: '350ms' }} />
                <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '60%', animationDelay: '500ms' }} />
              </div>
            </button>
          ) : dictatePending ? (
            /* Transcribing: full-width status bar */
            <div className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#9B6DD4]/10 border border-[#9B6DD4]/20">
              <Loader2 size={14} className="text-[#9B6DD4] animate-spin" />
              <span className="text-xs font-mono text-[#9B6DD4]/70">Transcribing...</span>
            </div>
          ) : (
            /* Idle: mic button */
            <div className="flex items-center gap-2">
              <button
                onClick={onDictate}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#9B6DD4]/10 border border-[#9B6DD4]/30 text-[#9B6DD4] text-xs font-mono hover:bg-[#9B6DD4]/20 transition-colors disabled:opacity-50"
              >
                <Mic size={14} />
                Voice
              </button>
            </div>
          )}
        </div>

        {/* Attr drawer */}
        {showAttrDrawer && (
          <div className="mt-3 pt-3 border-t border-white/[0.06]/30 space-y-2.5">
            {[
              { attr: 'urgency', opts: URGENCY_CYCLE, map: URGENCY_COLORS },
              { attr: 'domain', opts: DOMAIN_CYCLE, map: DOMAIN_COLORS },
            ].map(({ attr, opts, map }) => (
              <div key={attr} className="flex items-center gap-2">
                {opts.map(opt => {
                  const active = entry[attr] === opt || (opt === 'hot' && entry[attr] === 'burning') || (opt === 'cold' && entry[attr] === 'warm');
                  const color = map[opt] || '#b0b4c0';
                  return (
                    <button
                      key={opt}
                      onPointerDown={(e) => { e.stopPropagation(); onUpdateAttr(entry.id, attr, opt); }}
                      className="text-[11px] font-mono px-3 py-1 rounded-full transition-all select-none touch-manipulation"
                      style={active
                        ? { background: `${color}22`, color, border: `1px solid ${color}55` }
                        : { color: 'rgba(154,154,154,0.5)', border: '1px solid rgba(42,42,42,0.8)' }
                      }
                    >{opt}</button>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {/* Bottom row: attr drawer dots */}
        <div className="flex items-center justify-center mt-3 pt-3 border-t border-white/[0.06]/30">
          {/* Center: attr drawer trigger — 3 colored dots */}
          <button
            onPointerDown={(e) => { e.stopPropagation(); setShowAttrDrawer(d => !d); }}
            className="flex items-center gap-1.5 px-2 py-1 rounded-full transition-colors select-none touch-manipulation"
            style={{ background: showAttrDrawer ? 'rgba(107,53,184,0.1)' : 'transparent' }}
          >
            {[
              { val: entry.urgency, map: URGENCY_COLORS },
              { val: entry.domain, map: DOMAIN_COLORS },
            ].map(({ val, map }, i) => (
              <span key={i} className="w-2 h-2 rounded-full" style={{ backgroundColor: map[val] || 'rgba(154,154,154,0.3)' }} />
            ))}
          </button>
        </div>

        {/* OverflowMenu — expanded card */}
        <OverflowMenu
          items={[
            { icon: <Pencil size={16} strokeWidth={1.5} />, label: 'Edit title', onClick: () => setEditingTitle(true) },
            { icon: <Calendar size={16} strokeWidth={1.5} />, label: 'Set date', onClick: () => onCalendarTap(entry) },
            { icon: <Paperclip size={16} strokeWidth={1.5} />, label: 'Attach file', onClick: () => onAttachDoc(entry.id) },
            { icon: <Copy size={16} strokeWidth={1.5} />, label: 'Copy', onClick: () => {
              const text = `${entry.title || 'Untitled'}\n\n${entry.body || ''}`.trim();
              const el = document.createElement('textarea');
              el.value = text;
              el.setAttribute('readonly', '');
              el.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none';
              document.body.appendChild(el);
              el.focus();
              el.select();
              el.setSelectionRange(0, text.length);
              let syncOk = false;
              try { syncOk = document.execCommand('copy'); } catch {}
              document.body.removeChild(el);
              if (syncOk) {
                feedback.confirm();
              } else {
                navigator.clipboard?.writeText(text)
                  .then(() => feedback.confirm())
                  .catch(() => {});
              }
            }},
            { icon: entry.status === 'solved' ? <RotateCcw size={16} strokeWidth={1.5} /> : <Check size={16} strokeWidth={1.5} />, label: entry.status === 'solved' ? 'Reopen' : 'Mark as solved', onClick: onToggleSolved },
            { icon: <Trash2 size={16} strokeWidth={1.5} />, label: 'Delete', onClick: onDelete, destructive: true },
          ]}
        />

        {calendarForm?.entryId === entry.id && (
          <div
            onClick={e => e.stopPropagation()}
            style={{
              marginTop: 12,
              paddingTop: 12,
              borderTop: '0.5px solid rgba(255,255,255,0.06)',
              display: 'flex',
              flexDirection: 'column',
              gap: 10
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 10, color: '#b0b4c0', letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'monospace' }}>
                Calendar event
              </span>
              <button
                onClick={() => onCalendarFormChange(null)}
                style={{ color: '#454860', fontSize: 12, background: 'none', border: 'none', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <input
              value={calendarForm.event_title}
              onChange={e => onCalendarFormChange(f => ({ ...f, event_title: e.target.value }))}
              placeholder="Event title"
              style={{
                background: '#0a0a0f', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 8,
                padding: '8px 12px', color: '#f0f0f5', fontSize: 13, width: '100%',
                outline: 'none', fontFamily: 'system-ui'
              }}
            />

            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="date"
                value={calendarForm.due_date}
                onChange={e => onCalendarFormChange(f => ({ ...f, due_date: e.target.value }))}
                style={{
                  flex: 1, background: '#0a0a0f', border: '0.5px solid rgba(255,255,255,0.06)',
                  borderRadius: 8, padding: '8px 12px', color: '#f0f0f5',
                  fontSize: 13, outline: 'none', fontFamily: 'monospace'
                }}
              />
              <input
                type="time"
                value={calendarForm.due_time}
                onChange={e => onCalendarFormChange(f => ({ ...f, due_time: e.target.value }))}
                placeholder="Time (optional)"
                style={{
                  width: 120, background: '#0a0a0f', border: '0.5px solid rgba(255,255,255,0.06)',
                  borderRadius: 8, padding: '8px 12px', color: calendarForm.due_time ? '#f0f0f5' : '#454860',
                  fontSize: 13, outline: 'none', fontFamily: 'monospace'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <select
                value={calendarForm.alert_minutes}
                onChange={e => onCalendarFormChange(f => ({ ...f, alert_minutes: parseInt(e.target.value) }))}
                style={{
                  flex: 1, background: '#0a0a0f', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 8,
                  padding: '8px 12px', color: '#f0f0f5', fontSize: 13, outline: 'none',
                  fontFamily: 'system-ui'
                }}
              >
                <option value={15}>Alert: 15 min before</option>
                <option value={60}>Alert: 1 hour before</option>
                <option value={480}>Alert: 8 hours before</option>
                <option value={1440}>Alert: 1 day before</option>
                <option value={2880}>Alert: 2 days before</option>
                <option value={10080}>Alert: 1 week before</option>
              </select>
              {calendarForm.alert_minutes >= 1440 && !calendarForm.due_time && (
                <input
                  type="time"
                  value={calendarForm.alert_time || '09:00'}
                  onChange={e => onCalendarFormChange(f => ({ ...f, alert_time: e.target.value }))}
                  style={{
                    width: 110, background: '#0a0a0f', border: '0.5px solid rgba(255,255,255,0.06)',
                    borderRadius: 8, padding: '8px 12px', color: '#f0f0f5',
                    fontSize: 13, outline: 'none', fontFamily: 'monospace'
                  }}
                />
              )}
            </div>

            <button
              onClick={onCreateICS}
              style={{
                background: '#4B5FD4', color: '#f0f0f5', border: 'none',
                borderRadius: 999, padding: '8px 16px', fontSize: 12,
                cursor: 'pointer', fontFamily: 'system-ui', letterSpacing: '0.04em',
                alignSelf: 'flex-end'
              }}
            >
              Create .ics
            </button>
          </div>
        )}
        {/* Bottom-left accent line — visually closes the card */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '50%', height: '2px', background: `linear-gradient(to right, ${urgencyColor}, transparent)`, borderRadius: '0 999px 999px 0', pointerEvents: 'none' }} />
      </div>
    );
  }

  // Collapsed view - single line layout with prominent date
  const date = new Date(entry.updated_at || entry.created_at);
  const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
  const day = date.getDate();
  
  return (
    <div
      onClick={onToggle}
      className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3 cursor-pointer active:bg-white/[0.05] transition-colors select-none"
      style={{ position: 'relative', borderLeft: `2px solid ${urgencyColor}`, borderRight: entry.domain && DOMAIN_COLORS[entry.domain] ? `3px solid ${DOMAIN_COLORS[entry.domain]}80` : undefined, opacity: isSolved ? 0.45 : 1 }}
      data-testid={`entry-${entry.id}`}
    >
      <div className="flex items-center gap-3">
        {/* Solve / reopen */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleSolved(); }}
          aria-label={isSolved ? 'Reopen' : 'Mark as solved'}
          data-testid={`solve-${entry.id}`}
          className="shrink-0 w-11 h-11 -my-2 -ml-2 flex items-center justify-center touch-manipulation select-none"
        >
          <span
            style={{
              width: 20, height: 20, borderRadius: '50%',
              border: isSolved ? 'none' : `1.5px solid ${urgencyColor}`,
              background: isSolved ? GIRAFFE_GREEN : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 150ms ease',
            }}
          >
            {isSolved && <Check size={12} color="#fff" strokeWidth={2.5} />}
          </span>
        </button>

        {/* Date block */}
        <div className="flex flex-col items-center justify-center w-10 shrink-0">
          <span className="text-[9px] font-mono text-[#b0b4c0]/60 tracking-wider">{month}</span>
          <span className="text-lg font-medium text-[#f0f0f5] leading-none">{day}</span>
        </div>

        {/* Title */}
        <p className="text-sm font-medium text-[#f0f0f5] truncate flex-1 min-w-0">
          {entry.title || 'Untitled'}
        </p>

        {/* Spacer for overflow menu */}
        <div className="w-6 shrink-0" />
      </div>
      {/* OverflowMenu — collapsed card */}
      <OverflowMenu
        items={[
          { icon: <Pencil size={16} strokeWidth={1.5} />, label: 'Edit title', onClick: () => onToggle() },
          { icon: <Calendar size={16} strokeWidth={1.5} />, label: 'Set date', onClick: () => onCalendarTap(entry) },
          { icon: <Paperclip size={16} strokeWidth={1.5} />, label: 'Attach file', onClick: () => onAttachDoc(entry.id) },
          { icon: <Copy size={16} strokeWidth={1.5} />, label: 'Copy', onClick: () => {
            const text = `${entry.title || 'Untitled'}\n\n${entry.body || ''}`.trim();
            const el = document.createElement('textarea');
            el.value = text;
            el.setAttribute('readonly', '');
            el.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none';
            document.body.appendChild(el);
            el.focus();
            el.select();
            el.setSelectionRange(0, text.length);
            let syncOk = false;
            try { syncOk = document.execCommand('copy'); } catch {}
            document.body.removeChild(el);
            if (syncOk) {
              feedback.confirm();
            } else {
              navigator.clipboard?.writeText(text)
                .then(() => feedback.confirm())
                .catch(() => {});
            }
          }},
          { icon: entry.status === 'solved' ? <RotateCcw size={16} strokeWidth={1.5} /> : <Check size={16} strokeWidth={1.5} />, label: entry.status === 'solved' ? 'Reopen' : 'Mark as solved', onClick: onToggleSolved },
          { icon: <Trash2 size={16} strokeWidth={1.5} />, label: 'Delete', onClick: onDelete, destructive: true },
        ]}
      />
      {/* Bottom-left accent line — visually closes the card */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, width: '50%', height: '2px', background: `linear-gradient(to right, ${urgencyColor}, transparent)`, borderRadius: '0 999px 999px 0', pointerEvents: 'none' }} />
    </div>
  );
}

export default function Uncode() {
  // On subdomain (uncode.wernerdutoit.co.za) there is no Shell sidebar,
  // so fixed bars must span left-0 right-0. On main app they need md:left-64.
  const hasSidebar = !window.location.hostname.startsWith('uncode.');
  const { count: queueCount, openQueue } = useQueue();
  const feedback = useConfirmation();

  // State
  const [entries, setEntries] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({ urgency: null, domain: null });
  const [showSolved, setShowSolved] = useState(false);
  const [sortMode, setSortMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('uncode-sort') || 'heat';
    }
    return 'heat';
  });
  const [search, setSearch] = useState(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [newBody, setNewBody] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });
  
  // Inline expansion state
  const [expanded, setExpanded] = useState(null);
  const [editBody, setEditBody] = useState('');
  
  // Dictate state
  const [dictatePending, setDictatePending] = useState(false);
  const [recordingDictate, setRecordingDictate] = useState(false);

  // Calendar state
  const [calendarForm, setCalendarForm] = useState(null); // { entryId, event_title, due_date, due_time, alert_minutes, entry_body }

  // New entry attribute state
  const [newUrgency, setNewUrgency] = useState('cold');
  const [newEntryType, setNewEntryType] = useState('idea');
  const [newDomain, setNewDomain] = useState('matrix');

  // Document attach state
  const docFileInputRef = useRef(null);
  const [docAttachingEntryId, setDocAttachingEntryId] = useState(null);
  const [docUploading, setDocUploading] = useState(false);

  // Quick-add bar state
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickText, setQuickText] = useState('');
  const [quickAdding, setQuickAdding] = useState(false);
  const [quickKeyboardOffset, setQuickKeyboardOffset] = useState(0);
  const [quickBarDragging, setQuickBarDragging] = useState(false);
  const [quickRecording, setQuickRecording] = useState(false);
  const [quickTranscribing, setQuickTranscribing] = useState(false);
  const [quickUploading, setQuickUploading] = useState(false);

  // Undo pill after solving from the card
  const [undo, setUndo] = useState(null); // { id, title }
  const undoTimerRef = useRef(null);

  // Refs
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const textareaRef = useRef(null);
  const dictateRecorderRef = useRef(null);
  const sortedOrderRef = useRef([]);
  const dictateChunksRef = useRef([]);
  const quickInputRef = useRef(null);
  const quickFileRef = useRef(null);
  const quickMediaRef = useRef(null);
  const quickChunksRef = useRef([]);

  // Persist sort mode
  const handleSortChange = (mode) => {
    setSortMode(mode);
    localStorage.setItem('uncode-sort', mode);
  };

  // Load entries
  useEffect(() => {
    loadEntries();
  }, []);

  // Universal input listener
  useEffect(() => {
    const handler = (e) => { if (e.detail?.app === 'uncode') loadEntries(); };
    window.addEventListener('universal-input-applied', handler);
    return () => window.removeEventListener('universal-input-applied', handler);
  }, []);

  const loadEntries = async () => {
    try {
      const { data } = await api.get('/tasks');
      setEntries(data || []);
    } catch (err) {
      console.error('Failed to load entries:', err);
    }
  };

  // FAB event listener
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.app === 'uncode') setShowNew(true);
    };
    window.addEventListener('fab-new-entry', handler);
    return () => window.removeEventListener('fab-new-entry', handler);
  }, []);

  // Toggle card expansion
  const toggleExpand = (entry) => {
    if (expanded === entry.id) {
      setExpanded(null);
    } else {
      setExpanded(entry.id);
      // Remove title from body text (title is derived from first line,
      // sometimes with "..." appended — strip that before matching)
      const body = entry.body || '';
      const title = entry.title || '';
      const titleCore = title.endsWith('...') ? title.slice(0, -3).trimEnd() : title;
      const firstLine = body.split('\n')[0];
      // Only strip the title line when body has multiple lines — single-paragraph
      // entries (e.g. voice transcriptions) have body === first line, which starts
      // with the title, but stripping it would empty the body entirely.
      const firstLineIsTitle = body.includes('\n') && titleCore && (
        firstLine.startsWith(titleCore) || titleCore.startsWith(firstLine.slice(0, 20))
      );
      const bodyWithoutTitle = firstLineIsTitle ? body.slice(firstLine.length).trimStart() : body;
      setEditBody(bodyWithoutTitle);
    }
  };

  // Save body on blur
  const handleBodyBlur = async () => {
    if (!expanded) return;
    const entry = entries.find(e => e.id === expanded);
    // Reconstruct full body with title
    const fullBody = entry.title ? entry.title + '\n' + editBody : editBody;
    if (!entry || entry.body === fullBody) return;
    
    try {
      await api.put(`/tasks/${expanded}`, { body: fullBody });
      // Optimistic update
      setEntries(prev => prev.map(e => 
        e.id === expanded 
          ? { ...e, body: fullBody, updated_at: new Date().toISOString() }
          : e
      ));
    } catch {}
  };

  // Update attribute to specific value (desktop)
  const updateAttr = async (entryId, attr, value) => {
    const entry = entries.find(e => e.id === entryId);
    if (!entry || entry[attr] === value) return;
    const prevValue = entry[attr];
    
    // Optimistic update
    setEntries(prev => prev.map(e => 
      e.id === entryId 
        ? { ...e, [attr]: value, updated_at: new Date().toISOString() }
        : e
    ));
    
    try {
      await api.put(`/tasks/${entryId}`, { [attr]: value });
    } catch {
      // Revert on error
      setEntries(prev => prev.map(e => 
        e.id === entryId ? { ...e, [attr]: prevValue } : e
      ));
      feedback.fail();
    }
  };

  // Save editBody directly (used by checkbox toggles)
  const handleSaveEditBody = async (newBody) => {
    if (!expanded) return;
    const entry = entries.find(e => e.id === expanded);
    if (!entry) return;
    const fullBody = entry.title ? entry.title + '\n' + newBody : newBody;
    try {
      await api.put(`/tasks/${expanded}`, { body: fullBody });
      setEntries(prev => prev.map(e =>
        e.id === expanded ? { ...e, body: fullBody, updated_at: new Date().toISOString() } : e
      ));
    } catch {}
  };

  // Document attach handlers
  const handleOpenDocAttach = (entryId, directFile = null) => {
    if (directFile) {
      // Drag-and-drop: upload immediately without picker
      uploadDocFile(entryId, directFile);
    } else {
      setDocAttachingEntryId(entryId);
      docFileInputRef.current?.click();
    }
  };

  const uploadDocFile = async (entryId, file) => {
    setDocAttachingEntryId(entryId);
    setDocUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/tasks/${entryId}/documents`, formData);
      feedback.confirm();
    } catch (err) {
      console.error('uploadDocFile error:', err?.response?.data || err);
      feedback.fail();
    } finally {
      setDocUploading(false);
      setDocAttachingEntryId(null);
    }
  };

  const handleDocFileSelected = async (e) => {
    const file = e.target.files[0];
    if (!file || !docAttachingEntryId) return;
    e.target.value = '';
    await uploadDocFile(docAttachingEntryId, file);
  };

  // Cycle attribute to next value (mobile)
  const cycleAttr = async (entryId, attr) => {
    const entry = entries.find(e => e.id === entryId);
    if (!entry) return;
    const nextValue = getNextInCycle(attr, entry[attr]);
    await updateAttr(entryId, attr, nextValue);
  };

  // Dictate - record and append transcription to body
  const handleDictate = async () => {
    if (recordingDictate) {
      // Stop recording
      if (dictateRecorderRef.current) {
        dictateRecorderRef.current.stop();
      }
      return;
    }
    
    // Start recording
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      dictateRecorderRef.current = recorder;
      dictateChunksRef.current = [];
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) dictateChunksRef.current.push(e.data);
      };
      
      recorder.onstop = async () => {
        setRecordingDictate(false);
        setDictatePending(true);
        
        const blob = new Blob(dictateChunksRef.current, { type: 'audio/webm' });
        const formData = new FormData();
        formData.append('file', blob, 'recording.webm');
        
        try {
          const { data } = await api.post('/tasks/transcribe-voice', formData);
          // Append transcribed text to existing body
          setEditBody(prev => prev.trim() ? prev + '\n' + data.body : data.body);
        } catch (err) {
          feedback.fail();
        } finally {
          setDictatePending(false);
          stream.getTracks().forEach(track => track.stop());
        }
      };
      
      recorder.start();
      setRecordingDictate(true);
    } catch (err) {
      feedback.fail();
    }
  };

  // Calendar: open the event form prefilled with the title and today's date
  const handleCalendarTap = (entry) => {
    setCalendarForm({
      entryId: entry.id,
      event_title: entry.title,
      due_date: new Date().toISOString().split('T')[0],
      due_time: '',
      alert_minutes: 1440,
      alert_time: '09:00',
      entry_body: entry.body || ''
    });
  };

  // Calendar: generate and download .ics
  const handleCreateICS = async () => {
    if (!calendarForm) return;
    try {
      const response = await api.post(
        `/tasks/${calendarForm.entryId}/ics`,
        {
          event_title: calendarForm.event_title,
          due_date: calendarForm.due_date,
          due_time: calendarForm.due_time || null,
          alert_minutes: calendarForm.alert_minutes,
          alert_time: calendarForm.alert_time || '09:00',
          entry_body: calendarForm.entry_body
        },
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(
        new Blob([response.data], { type: 'text/calendar' })
      );
      const a = document.createElement('a');
      a.href = url;
      a.download = `${calendarForm.event_title.slice(0, 40).replace(/[^a-z0-9]/gi, '_')}.ics`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      feedback.confirm();
      setCalendarForm(null);
    } catch {
      feedback.fail();
    }
  };

  // Handle paste for images/files
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          e.preventDefault();
          const fileName = file.name || (file.type.startsWith('image/') ? 'Pasted image' : 'Pasted file');
          setNewBody(prev => prev + (prev ? '\n\n' : '') + `[Attached: ${fileName}]`);
          feedback.confirm();
          return;
        }
      }
    }
  };

  // Quick-add bar
  const openQuickAdd = () => {
    setQuickAddOpen(true);
    window.dispatchEvent(new CustomEvent('quick-add-focus'));
  };
  const closeQuickAdd = () => {
    setQuickAddOpen(false);
    setQuickText('');
    window.dispatchEvent(new CustomEvent('quick-add-blur'));
  };

  // Track keyboard height for quick-add popup
  useEffect(() => {
    if (!quickAddOpen) { setQuickKeyboardOffset(0); return; }
    const update = () => {
      const vv = window.visualViewport;
      if (vv) setQuickKeyboardOffset(Math.max(0, window.innerHeight - vv.height - (vv.offsetTop || 0)));
    };
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    update();
    return () => {
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
    };
  }, [quickAddOpen]);

  const startQuickRecording = async () => {
    if (quickRecording) { quickMediaRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      quickMediaRef.current = recorder;
      quickChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) quickChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        setQuickRecording(false);
        setQuickTranscribing(true);
        const blob = new Blob(quickChunksRef.current, { type: 'audio/webm' });
        const formData = new FormData();
        formData.append('file', blob, 'recording.webm');
        try {
          const { data } = await api.post('/tasks/transcribe-voice', formData);
          setQuickText(prev => prev.trim() ? prev + '\n' + data.body : data.body);
          // Auto-resize textarea to show full transcription
          requestAnimationFrame(() => {
            if (quickInputRef.current) {
              quickInputRef.current.style.height = 'auto';
              const maxPx = Math.round(window.innerHeight * 0.30);
              quickInputRef.current.style.height = Math.min(quickInputRef.current.scrollHeight, maxPx) + 'px';
            }
          });
        } catch { feedback.fail(); }
        finally { setQuickTranscribing(false); stream.getTracks().forEach(t => t.stop()); }
      };
      recorder.start();
      setQuickRecording(true);
    } catch { feedback.fail(); }
  };

  // Quick-add file: create an entry from the typed text (or filename), then attach the file.
  // Audio and text files become entry text instead, since documents only hold images/PDFs.
  const processQuickFile = async (file) => {
    const name = file.name || '';
    const isImage = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf' || name.toLowerCase().endsWith('.pdf');
    const isAudio = file.type.startsWith('audio/');
    const isText = file.type === 'text/plain' || name.toLowerCase().endsWith('.txt');
    if (!isImage && !isPdf && !isAudio && !isText) { feedback.fail(); return; }
    setQuickUploading(true);
    try {
      let body = quickText.trim();
      if (isAudio) {
        const formData = new FormData();
        formData.append('file', file, name || 'recording.webm');
        const { data } = await api.post('/tasks/transcribe-voice', formData);
        body = [body, data.body].filter(Boolean).join('\n');
      } else if (isText) {
        body = [body, (await file.text()).trim()].filter(Boolean).join('\n');
      }
      if (!body) body = name || (isImage ? 'Image' : 'File');
      const { data: newEntry } = await api.post('/tasks', { body, urgency: 'cold', entry_type: 'idea', domain: 'matrix', status: 'open' });
      if ((isImage || isPdf) && newEntry?.id) {
        const formData = new FormData();
        formData.append('file', file);
        await api.post(`/tasks/${newEntry.id}/documents`, formData);
      }
      if (newEntry) setEntries(prev => [newEntry, ...prev]);
      else loadEntries();
      closeQuickAdd();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      feedback.confirm();
    } catch (err) {
      console.error('processQuickFile error:', err?.response?.data || err);
      feedback.fail();
    } finally {
      setQuickUploading(false);
    }
  };

  const handleQuickSend = async () => {
    if (!quickText.trim()) return;
    setQuickAdding(true);
    try {
      const payload = { body: quickText, urgency: 'cold', entry_type: 'idea', domain: 'matrix', status: 'open' };
      const { data: newEntry } = await api.post('/tasks', payload);
      setQuickText('');
      if (quickInputRef.current) { quickInputRef.current.style.height = 'auto'; }
      if (newEntry) setEntries(prev => [newEntry, ...prev]);
      else loadEntries();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      feedback.confirm();
    } catch { feedback.fail(); }
    finally { setQuickAdding(false); }
  };

  // Toggle solved status
  const toggleSolved = async (entryId) => {
    const entry = entries.find(e => e.id === entryId);
    if (!entry) return;
    
    const newStatus = entry.status === 'solved' ? 'open' : 'solved';
    try {
      await api.put(`/tasks/${entryId}`, { status: newStatus });
      
      setEntries(prev => prev.map(e => 
        e.id === entryId 
          ? { ...e, status: newStatus, updated_at: new Date().toISOString() }
          : e
      ));
      
      setExpanded(null);
      feedback.confirm();
      if (newStatus === 'solved') {
        clearTimeout(undoTimerRef.current);
        setUndo({ id: entryId, title: entry.title || 'Untitled' });
        undoTimerRef.current = setTimeout(() => setUndo(null), 5000);
      }
    } catch {
      feedback.fail();
    }
  };

  // Clear the undo timer on unmount
  useEffect(() => () => clearTimeout(undoTimerRef.current), []);

  // Delete entry
  const deleteEntry = async () => {
    try {
      await api.delete(`/tasks/${deleteConfirm.id}`);
      setEntries(prev => prev.filter(e => e.id !== deleteConfirm.id));
      if (expanded === deleteConfirm.id) setExpanded(null);
      feedback.confirm();
    } catch {
      feedback.fail();
    }
  };

  // Voice recording for new entry - same as dictate, appends to body
  const startRecording = async () => {
    if (recording) {
      // Stop recording
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
      }
      return;
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      
      recorder.onstop = async () => {
        setRecording(false);
        setTranscribing(true);
        
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const formData = new FormData();
        formData.append('file', blob, 'recording.webm');
        
        try {
          const { data } = await api.post('/tasks/transcribe-voice', formData);
          setNewBody(prev => prev.trim() ? prev + '\n' + data.body : data.body);
          // Auto-resize textarea to show full transcription
          requestAnimationFrame(() => {
            if (textareaRef.current) {
              textareaRef.current.style.height = 'auto';
              textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
              textareaRef.current.focus();
            }
          });
        } catch (err) {
          feedback.fail();
        } finally {
          setTranscribing(false);
          stream.getTracks().forEach(track => track.stop());
        }
      };
      
      recorder.start();
      setRecording(true);
    } catch (err) {
      feedback.fail();
    }
  };

  // Save new entry
  const saveNewEntry = async () => {
    try {
      const payload = {
        body: newBody,
        urgency: newUrgency,
        entry_type: newEntryType,
        domain: newDomain,
        status: 'open',
      };

      const { data: newEntry } = await api.post('/tasks', payload);

      // Reset state
      setNewBody('');
      setNewUrgency('cold');
      setNewEntryType('idea');
      setNewDomain('matrix');
      setShowNew(false);
      if (newEntry) setEntries(prev => [newEntry, ...prev]);
      else loadEntries();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      feedback.confirm();
    } catch {
      feedback.fail();
    }
  };

  // Separate todos from notes
  // Detect todos by is_todo flag OR by "todo" keyword in title (backward compat for pre-existing entries)
  const isTodoEntry = (e) => e.is_todo || /\btodo\b/i.test(e.title || '');
  const allTodos = entries.filter(isTodoEntry);
  const activeTodos = allTodos.filter(e => e.status === 'open').sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const doneTodos = allTodos.filter(e => e.status === 'solved').sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));

  const handleAddTodo = async (text) => {
    try {
      await api.post('/tasks', {
        title: text,
        body: text,
        is_todo: true,
        urgency: 'cold',
        entry_type: 'task',
        domain: 'matrix',
        status: 'open',
      });
      // Reload all entries — backend may have split into multiple todos
      await loadEntries();
      feedback.confirm();
    } catch { feedback.fail(); }
  };

  const handleToggleTodoDone = async (todoId, newStatus) => {
    try {
      await api.put(`/tasks/${todoId}`, { status: newStatus });
      setEntries(prev => prev.map(e =>
        e.id === todoId ? { ...e, status: newStatus, updated_at: new Date().toISOString() } : e
      ));
      feedback.confirm();
    } catch { feedback.fail(); }
  };

  const handleDeleteTodo = async (todoId) => {
    try {
      await api.delete(`/tasks/${todoId}`);
      setEntries(prev => prev.filter(e => e.id !== todoId));
      feedback.confirm();
    } catch { feedback.fail(); }
  };

  const handleUpdateTodo = async (todoId, updates) => {
    try {
      await api.put(`/tasks/${todoId}`, updates);
      setEntries(prev => prev.map(e =>
        e.id === todoId ? { ...e, ...updates, updated_at: new Date().toISOString() } : e
      ));
    } catch { feedback.fail(); }
  };

  // Filtering and sorting (notes only, exclude todos unless searching)
  let visible = entries.filter(e => {
    if (search) {
      return e.body?.toLowerCase().includes(search.toLowerCase()) ||
             e.title?.toLowerCase().includes(search.toLowerCase());
    }
    if (isTodoEntry(e)) return false;
    if (showSolved && e.status !== 'solved') return false;
    if (!showSolved && e.status !== 'open') return false;
    if (filters.urgency && e.urgency !== filters.urgency) return false;
    if (filters.domain && e.domain !== filters.domain) return false;
    return true;
  });

  // Freeze sort order while a card is expanded to prevent jumps during editing
  if (!expanded) {
    if (sortMode === 'heat') {
      // hot (and legacy burning) first, cold (and legacy warm) second
      const urgencyOrder = { hot: 0, burning: 0, cold: 1, warm: 1 };
      visible.sort((a, b) => {
        const uDiff = (urgencyOrder[a.urgency] ?? 1) - (urgencyOrder[b.urgency] ?? 1);
        if (uDiff !== 0) return uDiff;
        return new Date(b.created_at) - new Date(a.created_at);
      });
    } else {
      visible.sort((a, b) => {
        const aTime = a.updated_at || a.created_at;
        const bTime = b.updated_at || b.created_at;
        return new Date(bTime) - new Date(aTime);
      });
    }
    sortedOrderRef.current = visible.map(e => e.id);
  } else if (sortedOrderRef.current.length) {
    // Preserve last sort order while editing
    const orderMap = {};
    sortedOrderRef.current.forEach((id, i) => { orderMap[id] = i; });
    visible.sort((a, b) => (orderMap[a.id] ?? 999) - (orderMap[b.id] ?? 999));
  }

  const canSave = newBody.trim();

  return (
    <div className="px-6 pt-3 pb-6 md:px-12 md:pt-4 md:pb-12 max-w-3xl mx-auto" data-testid="uncode-page">
      {/* Header — sticky so logo stays visible while scrolling */}
      <div className="sticky top-0 z-30 bg-[#0a0a0f] -mx-6 px-6 md:-mx-12 md:px-12 pt-3 md:pt-4 pb-4 mb-2">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center mb-1" style={{ gap: 2 }}>
            <img
              src="/logo-uncode.png"
              alt="Uncode"
              style={{ width: 36, height: 36, clipPath: 'url(#squircle)', flexShrink: 0 }}
            />
            <span style={{
              fontFamily: 'system-ui, sans-serif',
              fontSize: 28, fontWeight: 300,
              letterSpacing: '0.18em', textTransform: 'uppercase',
              color: '#f0f0f5', lineHeight: 1
            }}>NCODE</span>
          </div>
          <p className="text-[#b0b4c0] text-xs tracking-[0.2em] uppercase font-mono" style={{ paddingLeft: 42 }}>
            Chaos into Clarity
          </p>
        </div>
        {/* Search */}
        <div className="flex items-center gap-2">
          {search !== null && (
            <div className="flex items-center gap-1.5 bg-white/[0.03] border border-white/[0.06] rounded-lg px-2.5 py-1.5 transition-all">
              <Search size={12} className="text-[#b0b4c0]/50 shrink-0" />
              <input
                autoFocus
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="search..."
                className="bg-transparent text-[11px] font-mono text-[#f0f0f5] placeholder:text-[#b0b4c0]/40 outline-none w-28"
              />
              {search && (
                <button onClick={() => setSearch('')} className="text-[#b0b4c0]/40 hover:text-[#b0b4c0]">
                  <X size={10} />
                </button>
              )}
            </div>
          )}
          <button
            onClick={() => setSearch(s => s === null ? '' : null)}
            className={`p-1.5 rounded-lg transition-colors ${search !== null ? 'text-[#9B6DD4]' : 'text-[#b0b4c0]/40 hover:text-[#b0b4c0]'}`}
          >
            <Search size={16} />
          </button>
        </div>
      </div>
      </div>

      {/* Filter + Sort bar */}
      {(() => {
        const activeCount = Object.values(filters).filter(Boolean).length;
        return (
          <div className="mb-6">
            <div className="flex items-center gap-3">
              {/* Filter toggle */}
              <FilterToggle
                isExpanded={filterOpen}
                onToggle={() => setFilterOpen(o => !o)}
                activeCount={activeCount}
                accent="#E85D24"
              />

              {/* Right: sort */}
              <div className="ml-auto flex gap-1">
                {['heat', 'date'].map(mode => (
                  <button
                    key={mode}
                    onClick={() => handleSortChange(mode)}
                    className="text-[10px] font-mono px-2 py-0.5 rounded transition-colors"
                    style={sortMode === mode
                      ? { background: 'rgba(107,53,184,0.2)', color: '#9B6DD4' }
                      : { color: 'rgba(154,154,154,0.4)' }
                    }
                  >{mode}</button>
                ))}
              </div>
            </div>

            {/* Expanded filter chips */}
            {filterOpen && (
              <div className="mt-3 pt-3 border-t border-white/[0.06]/40">
                <div className="flex flex-wrap gap-2 items-center">
                  {/* open / solved */}
                  <div className="flex gap-1">
                    {['open', 'solved'].map(s => (
                      <button
                        key={s}
                        onClick={() => setShowSolved(s === 'solved')}
                        className="text-[10px] font-mono px-2 py-0.5 rounded transition-colors"
                        style={(s === 'solved') === showSolved
                          ? { background: 'rgba(107,53,184,0.2)', color: '#9B6DD4' }
                          : { color: 'rgba(154,154,154,0.4)' }
                        }
                      >{s}</button>
                    ))}
                  </div>
                  <div className="w-px h-4 bg-white/[0.06]" />
                  <ZFilterPills
                    options={URGENCY_CYCLE.map(u => ({ id: u, label: u }))}
                    selected={filters.urgency}
                    onChange={v => setFilters(f => ({ ...f, urgency: v }))}
                    accent="#E85D24"
                  />
                  <div className="w-px h-4 bg-white/[0.06]" />
                  <ZFilterPills
                    options={DOMAIN_CYCLE.map(d => ({ id: d, label: d }))}
                    selected={filters.domain}
                    onChange={v => setFilters(f => ({ ...f, domain: v }))}
                    accent="#E85D24"
                  />
                  {activeCount > 0 && (
                    <button
                      onClick={() => { setFilters({ urgency: null, domain: null }); setShowSolved(false); }}
                      className="text-[10px] font-mono text-[#b0b4c0]/30 hover:text-[#b0b4c0]/60 transition-colors ml-1"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Uncode queue banner */}
      {queueCount.by_app?.uncode > 0 && (
        <div style={{
          background: 'rgba(232,197,71,0.06)',
          border: '0.5px solid rgba(232,197,71,0.2)',
          borderRadius: 10,
          padding: '10px 14px',
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#E8C547', flexShrink: 0 }} />
          <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#b0b4c0', margin: 0, flex: 1 }}>
            {queueCount.by_app.uncode} item{queueCount.by_app.uncode !== 1 ? 's' : ''} waiting for review
          </p>
          <button
            onClick={() => openQueue('uncode')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#E8C547', padding: 0 }}
          >
            Review
          </button>
        </div>
      )}

      {/* Todo section */}
      {!search && (
        <TodoPanel
          todos={activeTodos}
          doneTodos={doneTodos}
          onToggleDone={handleToggleTodoDone}
          onDelete={handleDeleteTodo}
          onAdd={handleAddTodo}
          onUpdate={handleUpdateTodo}
        />
      )}

      {/* Entry list */}
      <div className="space-y-2" data-testid="entry-list">
        {visible.length === 0 ? (
          <ZEmptyState mark="U" accent="#6B35B8" message={search ? 'No matches' : showSolved ? 'No solved entries' : 'No open entries'} />
        ) : (
          visible.map(entry => (
            <EntryCard
              key={entry.id}
              entry={entry}
              isExpanded={expanded === entry.id}
              onToggle={() => toggleExpand(entry)}
              editBody={editBody}
              setEditBody={setEditBody}
              onBodyBlur={handleBodyBlur}
              onSaveEditBody={handleSaveEditBody}
              onUpdateAttr={updateAttr}
              onCycleAttr={cycleAttr}
              onToggleSolved={() => toggleSolved(entry.id)}
              onDelete={() => setDeleteConfirm({ open: true, id: entry.id, name: entry.title || 'this entry' })}
              onDictate={handleDictate}
              dictatePending={dictatePending}
              isRecordingDictate={recordingDictate}
              onCalendarTap={handleCalendarTap}
              calendarForm={calendarForm}
              onCalendarFormChange={setCalendarForm}
              onCreateICS={handleCreateICS}
              onAttachDoc={handleOpenDocAttach}
              docAttaching={docUploading && docAttachingEntryId === entry.id}
              feedback={feedback}
            />
          ))
        )}
      </div>

      {/* Hidden file input for document attach */}
      <input
        ref={docFileInputRef}
        type="file"
        accept="image/*,application/pdf"
        style={{ display: 'none' }}
        onChange={handleDocFileSelected}
      />

      {/* New Entry Bottom Sheet */}
      <Dialog open={showNew} onOpenChange={(v) => { setShowNew(v); if (!v) { setNewBody(''); setNewUrgency('cold'); setNewEntryType('idea'); setNewDomain('matrix'); } }}>
        <DialogContent className="bg-[#0a0a0f] border-white/[0.06] text-[#f0f0f5] max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg">New Entry</DialogTitle>
          </DialogHeader>
          
          <div className="mt-4 relative">
            {/* Textarea */}
            <Textarea
              ref={textareaRef}
              value={newBody}
              onChange={(e) => setNewBody(e.target.value)}
              onPaste={handlePaste}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  const pos = e.target.selectionStart;
                  const text = newBody;
                  const beforeCursor = text.slice(0, pos);
                  const lineStart = beforeCursor.lastIndexOf('\n') + 1;
                  const lineEndIdx = text.indexOf('\n', pos);
                  const currentLine = text.slice(lineStart, lineEndIdx === -1 ? undefined : lineEndIdx);
                  const isUnchecked = currentLine.startsWith('[] ');
                  const isChecked = currentLine.startsWith('[x] ');
                  if (isUnchecked || isChecked) {
                    const prefixLen = isUnchecked ? 3 : 4;
                    const lineContent = currentLine.slice(prefixLen);
                    if (!lineContent.trim()) {
                      e.preventDefault();
                      const newText = text.slice(0, lineStart) + text.slice(lineStart + prefixLen);
                      setNewBody(newText);
                      setTimeout(() => { e.target.selectionStart = e.target.selectionEnd = lineStart; }, 0);
                    } else {
                      e.preventDefault();
                      const newText = text.slice(0, pos) + '\n[] ' + text.slice(pos);
                      setNewBody(newText);
                      setTimeout(() => { e.target.selectionStart = e.target.selectionEnd = pos + 4; }, 0);
                    }
                  }
                }
                if (e.key === 'Backspace' && e.target.selectionStart === e.target.selectionEnd) {
                  const pos = e.target.selectionStart;
                  const text = newBody;
                  const beforeCursor = text.slice(0, pos);
                  const lineStart = beforeCursor.lastIndexOf('\n') + 1;
                  const lineEndIdx = text.indexOf('\n', pos);
                  const currentLine = text.slice(lineStart, lineEndIdx === -1 ? undefined : lineEndIdx);
                  const isUnchecked = currentLine.startsWith('[] ');
                  const isChecked = currentLine.startsWith('[x] ');
                  if (isUnchecked || isChecked) {
                    const prefixLen = isUnchecked ? 3 : 4;
                    if (!currentLine.slice(prefixLen).trim() && pos === lineStart + prefixLen) {
                      e.preventDefault();
                      const newText = text.slice(0, lineStart) + text.slice(lineStart + prefixLen);
                      setNewBody(newText);
                      setTimeout(() => { e.target.selectionStart = e.target.selectionEnd = lineStart; }, 0);
                    }
                  }
                }
              }}
              className="w-full min-h-[120px] bg-transparent border-white/[0.06] text-base leading-relaxed text-[#f0f0f5] font-serif focus-visible:ring-[#6B35B8]/50"
              placeholder="Type or paste images..."
              autoFocus
            />

            {/* Mic / recording controls */}
            <div className="mt-3" data-testid="mic-btn">
              {recording ? (
                <button
                  onClick={startRecording}
                  className="w-full flex items-center justify-center gap-3 py-3.5 rounded-xl bg-[#E8450A]/15 border border-[#E8450A]/40 transition-all active:scale-[0.98]"
                >
                  <div className="flex items-center gap-[3px] h-[18px]">
                    <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '60%', animationDelay: '0ms' }} />
                    <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '100%', animationDelay: '150ms' }} />
                    <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '40%', animationDelay: '300ms' }} />
                    <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '80%', animationDelay: '450ms' }} />
                  </div>
                  <span className="text-sm font-mono text-[#E8450A] font-medium tracking-wide">STOP</span>
                  <div className="flex items-center gap-[3px] h-[18px]">
                    <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '80%', animationDelay: '100ms' }} />
                    <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '40%', animationDelay: '250ms' }} />
                    <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '100%', animationDelay: '350ms' }} />
                    <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '60%', animationDelay: '500ms' }} />
                  </div>
                </button>
              ) : transcribing ? (
                <div className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-[#9B6DD4]/10 border border-[#9B6DD4]/20">
                  <Loader2 size={14} className="text-[#9B6DD4] animate-spin" />
                  <span className="text-xs font-mono text-[#9B6DD4]/70">Transcribing...</span>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono text-[#b0b4c0]/40">tap to dictate · paste images</span>
                  <button
                    onClick={startRecording}
                    className="w-12 h-12 rounded-full flex items-center justify-center bg-white/[0.05] border border-white/[0.06] hover:border-[#6B35B8]/50 transition-all"
                  >
                    <Mic size={20} className="text-[#b0b4c0]" />
                  </button>
                </div>
              )}
            </div>

            {/* Attribute row */}
            <div className="flex items-center justify-between mt-4 px-1">
              <div className="flex items-center gap-3">
                {/* Urgency */}
                <div className="flex gap-2">
                  {URGENCY_CYCLE.map(opt => (
                    <button
                      key={opt}
                      onClick={() => setNewUrgency(opt)}
                      className="text-[10px] font-mono transition-all"
                      style={{
                        color: newUrgency === opt ? URGENCY_COLORS[opt] : 'rgba(154,154,154,0.3)',
                        borderBottom: newUrgency === opt ? `1px solid ${URGENCY_COLORS[opt]}` : '1px solid transparent',
                        paddingBottom: '1px'
                      }}
                    >{opt}</button>
                  ))}
                </div>
                <span className="text-[#b0b4c0]/20 text-[10px]">·</span>
                {/* Domain */}
                <div className="flex gap-2">
                  {DOMAIN_CYCLE.map(opt => (
                    <button
                      key={opt}
                      onClick={() => setNewDomain(opt)}
                      className="text-[10px] font-mono transition-all"
                      style={{
                        color: newDomain === opt ? DOMAIN_COLORS[opt] : 'rgba(154,154,154,0.3)',
                        borderBottom: newDomain === opt ? `1px solid ${DOMAIN_COLORS[opt]}` : '1px solid transparent',
                        paddingBottom: '1px'
                      }}
                    >{opt}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* Save button */}
            <Button
              onClick={saveNewEntry}
              disabled={!newBody.trim()}
              className="w-full mt-3 bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full disabled:opacity-50"
              data-testid="save-btn"
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <DeleteConfirmDialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null, name: '' })}
        onConfirm={deleteEntry}
        itemName={deleteConfirm.name}
        itemType="entry"
      />

      {/* Undo pill — sits just above the quick-add bar */}
      {undo && !quickAddOpen && (
        <div className={`fixed bottom-[136px] md:bottom-24 left-0 ${hasSidebar ? 'md:left-64' : ''} right-0 z-30 pointer-events-none flex justify-center`}>
          <div
            className="pointer-events-auto flex items-center gap-3 rounded-full px-4 py-2 border border-white/[0.08] backdrop-blur-xl max-w-[80%]"
            style={{ background: 'rgba(10,10,15,0.9)' }}
          >
            <Check size={14} color={GIRAFFE_GREEN} />
            <span className="text-xs text-[#f0f0f5]/80 truncate">{undo.title}</span>
            <button
              onClick={() => { clearTimeout(undoTimerRef.current); const id = undo.id; setUndo(null); toggleSolved(id); }}
              className="text-xs font-mono text-[#9B6DD4] shrink-0"
            >
              Undo
            </button>
          </div>
        </div>
      )}

      {/* Quick-Add Trigger Bar — coral border, centered 70% */}
      {!quickAddOpen && (
        <div className={`fixed bottom-[72px] md:bottom-8 left-0 ${hasSidebar ? 'md:left-64' : ''} right-0 z-30 pointer-events-none flex justify-center`}>
          <div className="w-[70%] max-w-xl">
          <div
            onDragOver={(e) => { e.preventDefault(); setQuickBarDragging(true); }}
            onDragLeave={() => setQuickBarDragging(false)}
            onDrop={(e) => { e.preventDefault(); setQuickBarDragging(false); const file = e.dataTransfer.files?.[0]; if (file) processQuickFile(file); }}
            className={`pointer-events-auto w-full flex items-center gap-3 border-2 rounded-xl px-4 py-3 shadow-xl active:scale-[0.99] transition-all backdrop-blur-xl ${quickUploading ? 'z-bar-uploading' : quickBarDragging ? 'border-[#9B6DD4]' : 'border-[#E8450A]/80 hover:border-[#E8450A]'}`}
            style={{ background: 'rgba(10, 10, 15, 0.80)' }}
          >
            <span onClick={quickUploading ? undefined : openQuickAdd} className={`flex-1 text-sm text-white font-medium ${quickUploading ? 'cursor-wait' : 'cursor-pointer'}`}>
              {quickUploading ? 'Uploading…' : quickBarDragging ? 'Drop to attach…' : 'Uncode this…'}
            </span>
            {quickUploading
              ? <div className="w-5 h-5 border-2 border-white/30 border-t-white/80 rounded-full animate-spin shrink-0" />
              : (
                <button onClick={(e) => { e.stopPropagation(); openQuickAdd(); setTimeout(() => startQuickRecording(), 300); }} className="shrink-0 p-1">
                  <Mic size={22} className="text-white/70" />
                </button>
              )
            }
          </div>
          </div>
        </div>
      )}

      {/* Quick-Add Popup — sits above keyboard */}
      {quickAddOpen && (
        <>
          <div className="fixed inset-0 z-[55]" onClick={closeQuickAdd} />
          <div
            className={`fixed left-0 ${hasSidebar ? 'md:left-64' : ''} right-0 z-[60] pb-2`}
            style={{ bottom: quickKeyboardOffset }}
            onClick={(e) => e.stopPropagation()}
            onDragOver={(e) => { e.preventDefault(); setQuickBarDragging(true); }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setQuickBarDragging(false); }}
            onDrop={async (e) => { e.preventDefault(); setQuickBarDragging(false); const file = e.dataTransfer.files?.[0]; if (file) processQuickFile(file); }}
          >
            <div className="w-[70%] max-w-xl mx-auto">
            <div
              className={`w-full border-2 rounded-2xl shadow-2xl overflow-hidden transition-colors backdrop-blur-xl ${
                quickUploading ? 'z-bar-uploading' : quickBarDragging ? 'border-[#9B6DD4]' : quickRecording ? 'border-red-500/80' : 'border-[#E8450A]/80'
              }`}
              style={{ background: 'rgba(10, 10, 15, 0.92)' }}
            >
              <div className="flex items-center gap-2 px-4 pt-3 pb-1">
                <span className="text-[10px] font-mono text-white tracking-widest uppercase flex-1">Uncode this</span>
                <button onClick={closeQuickAdd} className="p-1 text-white/30 hover:text-white/70 transition-colors">
                  <X size={16} />
                </button>
              </div>
              <div className="px-4 pb-3">
                <textarea
                  ref={quickInputRef}
                  autoFocus
                  value={quickText}
                  rows={1}
                  onChange={(e) => {
                    setQuickText(e.target.value);
                    e.target.style.height = 'auto';
                    // Cap growth at 30vh so the popup never runs off the top of the viewport.
                    const maxPx = Math.round(window.innerHeight * 0.30);
                    e.target.style.height = Math.min(e.target.scrollHeight, maxPx) + 'px';
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleQuickSend(); } }}
                  onPaste={(e) => {
                    const items = e.clipboardData?.items;
                    if (!items) return;
                    for (let i = 0; i < items.length; i++) {
                      if (items[i].kind === 'file') {
                        const file = items[i].getAsFile();
                        if (file) { e.preventDefault(); processQuickFile(file); return; }
                      }
                    }
                  }}
                  placeholder={quickUploading ? 'Uploading…' : quickBarDragging ? 'Drop to attach…' : quickRecording ? 'Recording… tap Stop when done' : quickTranscribing ? 'Transcribing…' : 'Brain dump, idea, task, link…'}
                  disabled={quickRecording || quickAdding || quickUploading || quickTranscribing}
                  className="w-full bg-transparent text-[17px] text-[#f0f0f5] placeholder-white/25 focus:outline-none font-light resize-none"
                  style={{ minHeight: '28px', maxHeight: '30vh', overflowY: 'auto' }}
                />
              </div>
              <div className="border-t border-white/[0.07]">
                {quickRecording ? (
                  /* Recording: full-width stop bar */
                  <button
                    onClick={startQuickRecording}
                    className="w-full flex items-center justify-center gap-3 py-4 transition-all active:scale-[0.98]"
                    style={{ background: 'rgba(232,69,10,0.12)' }}
                  >
                    <div className="flex items-center gap-[3px] h-[18px]">
                      <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '60%', animationDelay: '0ms' }} />
                      <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '100%', animationDelay: '150ms' }} />
                      <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '40%', animationDelay: '300ms' }} />
                      <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '80%', animationDelay: '450ms' }} />
                    </div>
                    <span className="text-sm font-mono text-[#E8450A] font-medium tracking-wide">STOP</span>
                    <div className="flex items-center gap-[3px] h-[18px]">
                      <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '80%', animationDelay: '100ms' }} />
                      <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '40%', animationDelay: '250ms' }} />
                      <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '100%', animationDelay: '350ms' }} />
                      <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '60%', animationDelay: '500ms' }} />
                    </div>
                  </button>
                ) : quickTranscribing ? (
                  <div className="w-full flex items-center justify-center gap-2 py-4">
                    <Loader2 size={14} className="text-[#9B6DD4] animate-spin" />
                    <span className="text-xs font-mono text-[#9B6DD4]/70">Transcribing...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={startQuickRecording}
                        disabled={quickUploading}
                        className="flex items-center gap-1.5 text-white/55 hover:text-white transition-colors"
                      >
                        <Mic size={20} />
                        <span className="text-xs font-mono">Voice</span>
                      </button>
                      <label className="flex items-center gap-1.5 text-white/55 hover:text-white transition-colors cursor-pointer">
                        {quickUploading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white/80 rounded-full animate-spin" /> : <Upload size={20} />}
                        <span className="text-xs font-mono">Attach</span>
                        <input ref={quickFileRef} type="file" accept="image/*,audio/*,.png,.jpg,.jpeg,.webp,.heic,.mp3,.m4a,.wav,.webm,.ogg,.pdf,.txt" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) processQuickFile(f); }} />
                      </label>
                    </div>
                    {quickText.trim() ? (
                      <button onClick={handleQuickSend} disabled={quickAdding} className="flex items-center gap-2 px-4 py-2 bg-[#E8450A] hover:bg-[#E8450A]/90 text-white rounded-full text-sm font-mono transition-colors">
                        {quickAdding ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <><Send size={13} className="shrink-0" /> Send</>}
                      </button>
                    ) : (
                      <button onClick={closeQuickAdd} className="text-xs font-mono text-white/30 hover:text-white/60 px-2 py-1 transition-colors">Cancel</button>
                    )}
                  </div>
                )}
              </div>
            </div>
            </div>
          </div>
        </>
      )}

    </div>
  );
}
